// src/pages/ambulance/AmbulanceDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Ambulance,
  Car,
  Users,
  TrendingUp,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Activity,
  Bell,
  Settings,
  RefreshCw,
  ChevronRight,
  Phone,
  Navigation as NavigationIcon,
  Package
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AmbulanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [error, setError] = useState('');
  const [needsProfile, setNeedsProfile] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      // Fetch profile first to check if it exists
      const profileResponse = await apiService.ambulances.getProfile();
      
      if (profileResponse.needsProfile) {
        setNeedsProfile(true);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (!profileResponse.success) {
        throw new Error(profileResponse.message || 'Failed to load profile');
      }

      setProfile(profileResponse.data);

      // Fetch stats and requests in parallel
      const [statsResponse, requestsResponse] = await Promise.all([
        apiService.ambulances.getStats(),
        apiService.ambulances.getRequests({ status: 'pending', limit: 5 })
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (requestsResponse.success) {
        setPendingRequests(requestsResponse.data.requests || []);
      }
    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      hour: 3600,
      minute: 60
    };

    if (seconds < 60) return 'Just now';
    if (seconds < intervals.hour) {
      const minutes = Math.floor(seconds / intervals.minute);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    const hours = Math.floor(seconds / intervals.hour);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  };

  // Profile setup required
  if (needsProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Ambulance className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Profile</h2>
          <p className="text-gray-600 mb-6">
            Set up your ambulance service profile to start receiving requests and managing operations.
          </p>
          <button
            onClick={() => navigate('/ambulance/profile/setup')}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
          >
            Set Up Profile
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Requests Today',
      value: stats?.requests?.today || 0,
      icon: Ambulance,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      link: '/ambulance/requests',
      change: '+12%'
    },
    {
      title: 'Active Requests',
      value: stats?.requests?.active || 0,
      icon: Activity,
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      link: '/ambulance/requests?status=active',
      urgent: stats?.requests?.active > 0
    },
    {
      title: 'Available Vehicles',
      value: `${stats?.vehicles?.available || 0}/${stats?.vehicles?.total || 0}`,
      icon: Car,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      link: '/ambulance/vehicles'
    },
    {
      title: 'Available Drivers',
      value: `${stats?.drivers?.available || 0}/${stats?.drivers?.total || 0}`,
      icon: Users,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      link: '/ambulance/drivers'
    },
    {
      title: 'Total Trips',
      value: stats?.ambulance?.totalTrips || 0,
      icon: TrendingUp,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
      link: '/ambulance/analytics'
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(stats?.revenue?.thisMonth || 0),
      icon: DollarSign,
      bgColor: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      link: '/ambulance/earnings'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="bg-red-100 p-3 rounded-lg">
                <Ambulance className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {profile?.name || 'Ambulance Dashboard'}
                </h1>
                <p className="text-gray-600 mt-1 flex items-center gap-2">
                  {profile?.isIndependent ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Independent Service
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 text-blue-600" />
                      Affiliated with {profile?.hospital?.name}
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                title="Refresh dashboard"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <Link
                to="/ambulance/notifications"
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition relative"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {pendingRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {pendingRequests.length}
                  </span>
                )}
              </Link>
              <Link
                to="/ambulance/settings"
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>
              <Link
                to="/ambulance/profile"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
              >
                View Profile
              </Link>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="space-y-4 mb-6">
          {/* Subscription Alert */}
          {stats?.subscription?.status === 'expired' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900">Subscription Expired</h3>
                <p className="text-sm text-red-700 mt-1">
                  Your subscription has expired. Renew now to continue adding drivers and vehicles.
                </p>
                <Link
                  to="/ambulance/subscription"
                  className="inline-block mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold"
                >
                  Renew Subscription
                </Link>
              </div>
            </div>
          )}

          {/* Verification Alert */}
          {profile && !profile.isVerified && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-yellow-900">Verification Pending</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Your ambulance service is pending verification. Complete all required documents to get verified.
                </p>
                <Link
                  to="/ambulance/verification"
                  className="inline-block mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-semibold"
                >
                  Complete Verification
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link
                key={index}
                to={stat.link}
                className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all hover:-translate-y-1 relative"
              >
                {stat.urgent && (
                  <div className="absolute top-4 right-4 w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                  </div>
                  {stat.change && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                      {stat.change}
                    </span>
                  )}
                </div>
                <h3 className="text-gray-600 text-sm font-medium">{stat.title}</h3>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </Link>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Pending Requests - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-600" />
                Pending Requests
                {pendingRequests.length > 0 && (
                  <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-semibold">
                    {pendingRequests.length}
                  </span>
                )}
              </h2>
              <Link
                to="/ambulance/requests"
                className="text-sm text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
              >
                View All
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="p-6">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-gray-900 font-semibold mb-1">All Clear!</p>
                  <p className="text-gray-600 text-sm">No pending requests at the moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div
                      key={request._id}
                      className="border-2 border-gray-200 rounded-xl p-4 hover:border-red-300 transition-all hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              request.priority === 'critical' ? 'bg-red-100 text-red-700' :
                              request.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {request.priority?.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {getTimeAgo(request.requestedAt)}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-900 mb-1">
                            {request.patientInfo?.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3">
                            {request.patientInfo?.condition}
                          </p>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">
                                <span className="font-medium">Pickup:</span> {request.pickupLocation?.address}
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">
                                <span className="font-medium">Dropoff:</span> {request.dropoffLocation?.address}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Link
                            to={`/ambulance/requests/${request._id}`}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold whitespace-nowrap"
                          >
                            Accept Request
                          </Link>
                          {request.patient?.phone && (
                            <a
                              href={`tel:${request.patient.phone}`}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center justify-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              Call
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subscription Status - Takes 1 column */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Subscription</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Current Plan</p>
                <p className="text-xl font-bold text-gray-900 capitalize mt-1">
                  {stats?.subscription?.plan || 'Free'}
                </p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Vehicles</span>
                    <span className="font-semibold text-gray-900">
                      {stats?.limits?.currentVehicles || 0} / {stats?.limits?.maxVehicles || 1}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${((stats?.limits?.currentVehicles || 0) / (stats?.limits?.maxVehicles || 1)) * 100}%`
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Drivers</span>
                    <span className="font-semibold text-gray-900">
                      {stats?.limits?.currentDrivers || 0} / {stats?.limits?.maxDrivers || 1}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${((stats?.limits?.currentDrivers || 0) / (stats?.limits?.maxDrivers || 1)) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>

              {stats?.subscription?.plan === 'free' && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-3">
                    Upgrade to add more vehicles and drivers
                  </p>
                  <Link
                    to="/ambulance/subscription"
                    className="block w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold text-center"
                  >
                    Upgrade Plan
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/ambulance/vehicles"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all hover:-translate-y-1"
          >
            <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Car className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Manage Vehicles</h3>
            <p className="text-sm text-gray-600">Add, edit, or remove ambulance vehicles</p>
          </Link>

          <Link
            to="/ambulance/drivers"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all hover:-translate-y-1"
          >
            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Manage Drivers</h3>
            <p className="text-sm text-gray-600">Add, edit, or manage driver profiles</p>
          </Link>

          <Link
            to="/ambulance/tracking"
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all hover:-translate-y-1"
          >
            <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <NavigationIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Live Tracking</h3>
            <p className="text-sm text-gray-600">Track active ambulances in real-time</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AmbulanceDashboard;